/**
 * 认证相关的工具函数
 */

import { oneDayInMs } from "@/constant";

/**
 * 从 Cookie 中获取 access_token
 * @returns {string | null} 返回 access_token 或 null
 */
export function getAccessToken(): string | null {
  try {
    // 获取所有 cookies
    const cookies = document.cookie.split(";");

    // 查找 access_token cookie
    const tokenCookie = cookies.find((cookie) => {
      const trimmedCookie = cookie.trim();
      return trimmedCookie.startsWith("access_token=");
    });

    if (tokenCookie) {
      // 提取 token 值
      const token = tokenCookie.split("=")[1];
      return token ? decodeURIComponent(token) : null;
    }

    return null;
  } catch (error) {
    console.error("获取 access_token 失败:", error);
    return null;
  }
}

/**
 * 检查用户是否已登录（是否有有效的 access_token）
 * @returns {boolean} 是否已登录
 */
export function isAuthenticated(): boolean {
  const token = getAccessToken();
  return !!token && token.trim() !== "";
}

/**
 * 获取 access_token 并添加 Bearer 前缀（用于 API 请求）
 * @returns {string | null} 返回 "Bearer {token}" 格式的字符串或 null
 */
export function getBearerToken(): string | null {
  const token = getAccessToken();
  return token ? `Bearer ${token}` : null;
}

/**
 * 从 Cookie 中获取指定名称的值
 * @param {string} name - Cookie 名称
 * @returns {string | null} Cookie 值或 null
 */
export function getCookieValue(name: string): string | null {
  try {
    const cookies = document.cookie.split(";");
    const cookie = cookies.find((c) => {
      const trimmedCookie = c.trim();
      return trimmedCookie.startsWith(`${name}=`);
    });

    return cookie ? decodeURIComponent(cookie.split("=")[1]) : null;
  } catch (error) {
    console.error(`获取 Cookie ${name} 失败:`, error);
    return null;
  }
}

/**
 * 设置 Cookie
 * @param {string} name - Cookie 名称
 * @param {string} value - Cookie 值
 * @param {number} days - 过期天数（可选）
 * @param {string} domain - 域名（可选）
 * @param {string} path - 路径（可选）
 */
export function setCookie(
  name: string,
  value: string,
  days?: number,
  domain?: string,
  path: string = "/"
): void {
  try {
    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * oneDayInMs);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    cookieString += `; path=${path}`;

    document.cookie = cookieString;
  } catch (error) {
    console.error("设置 Cookie 失败:", error);
  }
}

/**
 * 删除指定的 Cookie
 * @param {string} name - Cookie 名称
 * @param {string} domain - 域名（可选）
 * @param {string} path - 路径（可选）
 */
export function removeCookie(
  name: string,
  domain?: string,
  path: string = "/"
): void {
  try {
    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    console.error("删除 Cookie 失败:", error);
  }
}

/**
 * 清除所有认证相关的 Cookie
 */
export function clearAuthCookies(): void {
  const authCookieNames = [
    "access_token",
    "refresh_token",
    "auth_token",
    "token",
    "session_id",
    "user_id",
  ];

  authCookieNames.forEach((name) => {
    removeCookie(name);
  });
}
